import React from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import { css } from 'styled-components';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { PictureWithFallback } from 'components/Picture/PictureWithFallback';
import { Marker } from 'types/Asset';
import { hexToRgba } from 'utils/fnColors';
import { ButtonPlayOutline } from 'components/Buttons/ButtonPlayOutline/ButtonPlayOutline';
import { Breakpoints, MQ } from './cssMediaQueries';
import { fontBold, fontExtraBold, fontNormal, maxZIndex } from './cssGlobalVariables';
import { buttonReset } from './cssButtonReset';

export const CardImage: React.FC<{ url: string; alwaysLoad?: boolean; fallbackImage?: string; alt?: string }> = ({
    url,
    alwaysLoad,
    fallbackImage,
    ...rest
}) => <PictureWithFallback src={url} fit="cover" {...rest} fallbackImage={fallbackImage} alwaysLoad={alwaysLoad} />;

export const Clear = styled.div`
    clear: both;
    content: '';
    display: inline-block;
`;

export const LoadingIndicatorWrapper = styled.div`
    height: auto;
    padding-top: 50px;
`;

export const LoaderWrapper = styled.div`
    animation: flickerAnimation 1.2s infinite ease-in-out;
    animation-delay: 0.6s;
    position: relative;
    width: 100%;
    overflow: hidden;

    @keyframes flickerAnimation {
        0% {
            opacity: 1;
        }
        50% {
            opacity: 0.6;
        }
        100% {
            opacity: 1;
        }
    }
`;

export const LoadingRectangle = styled.div`
    width: 100%;
    height: 100%;
    background: ${(props) => hexToRgba(props.theme.brandedConfig.low_contrast, 0.8)};
    border-radius: 4px;
`;

export const TitlePlaceholder = styled.div`
    width: 80%;
    height: 12px;
    margin-top: 8px;
`;

export const LoadingCircle = styled.div`
    width: 100%;
    height: 100%;
    background: ${(props) => hexToRgba(props.theme.brandedConfig.low_contrast, 0.8)};
    border-radius: 50%;
`;

// page title with back icon
export const PageTitleBlock = styled.div`
    margin-bottom: 8px;
    position: relative;
    margin-left: 8px;
    margin-right: 8px;
    visibility: hidden;

    ${MQ(Breakpoints.m)} {
        visibility: visible;
    }
`;

export const PageTitle = styled.div`
    text-align: left;
    font-size: 14px;
    line-height: 20px;
`;

export const PageTitleText = styled.div`
    display: inline-block;
    vertical-align: middle;
`;

export const PageTitleSVG = styled(SVGInline)`
    height: 20px;

    svg {
        width: auto;
        height: 100%;
    }
`;

export const MetaDataSeparator = styled.span`
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    opacity: 0.7;
    display: inline-block;
    padding-left: 9px;
    padding-right: 9px;
    vertical-align: middle;
`;

export const CardBottomGradient = styled.span`
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 30%;
    z-index: 1;
    opacity: 0.32;
    pointer-events: none;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    background: linear-gradient(to top, var(--color-dark-background), transparent);
`;

export const ContentMarker = styled.div<{ marker: Marker; withProgressBar?: boolean }>`
    position: absolute;
    padding: 2px 6px;
    height: 20px;
    font-size: 12px;
    gap: 4px;
    font-family: ${fontBold};
    background-color: ${(props) => props.marker?.backgroundColor || 'var(--color-accent-dark)'};
    color: ${(props) => props.marker?.textColor || 'var(--color-primary)'};
    left: 8px;
    bottom: ${(props) => (props.withProgressBar ? '23px' : '8px')};
    z-index: 2;
    border-radius: 2px;
    display: flex !important;
    align-items: center;
    justify-content: center;
`;

export const ContentMarkerIcon = styled.span`
    display: inline-flex;
    width: 16px;
    height: 16px;
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const DetailMarkerAndMetaData = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${MQ(Breakpoints.xs)} {
        align-items: flex-start;
    }
`;

export const DetailMarkerContainer = styled.div``;

export const NoContentMessage = styled.div`
    color: var(--color-primary);
    font-family: ${fontExtraBold};
    line-height: 32px;
    width: 100%;
    padding: 0 12px;
    text-align: center;
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    /* From Figma. Should be latest..? */
    font-size: 20px;
    letter-spacing: 0.5%;
    max-width: 400px;
`;

export const NoContentMessageTitle = styled.p`
    color: var(--color-primary);
    font-family: ${fontExtraBold};
    line-height: 32px;
    margin-bottom: 8px;
`;

export const NoContentMessageSubtitle = styled.p`
    color: var(--Text-secondary, rgba(235, 235, 241, 0.7));
    text-align: center;
    font-family: ${fontNormal};
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: 0.08px;
`;

export const StyledSVG = styled(SVGInline)`
    margin-top: 3px;
    width: auto;
    display: inline-block;
    margin-right: 10px;
    position: relative;
    vertical-align: middle;
`;

export const LockIconSVG = styled(SVGInline)`
    height: 16px;
    width: 20px;
    margin-top: -6px;
    display: inline-block;
    margin-right: 4px;
    margin-left: -4px;
    position: relative;
    vertical-align: middle;
`;

export const StyledButtonPlayOutline = styled(ButtonPlayOutline)`
    ${MQ(Breakpoints.s)} {
        display: none;
    }
`;

export const ellipsis = (lineLimit: number) => css`
    display: -webkit-box;
    -webkit-line-clamp: ${lineLimit};
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
`;

export const AudioSubtitleBlock = styled.div`
    font-size: 14px;
    line-height: 20px;
    font-family: ${fontBold};
    color: var(--color-secondary);
`;

export const LandscapeImageContainer = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: #232438;
`;

export const MoviePortraitImageWrapper = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
`;

export const MoviePortraitImage = styled.div`
    position: absolute;
    width: 120px;
    height: 100%;
    left: 50%;
    transform: translateX(-50%);
    top: 0;

    img {
        width: 100%;
    }
`;

export const ChannelLogoWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 117px;
    height: 64px;
    border-top-right-radius: 3px;
    background: linear-gradient(214deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 25%, transparent 50%);
`;

export const ChannelImageInnerWrapper = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    height: 27px;
    max-width: 48px;
    text-align: right;
    font-size: 0;
`;

// modal

export const ModalWrapper = styled.div<{ opacity?: number }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    height: 100lvh;
    background: color-mix(in srgb, var(--color-dark-background), transparent 20%);
    backdrop-filter: blur(10px);
    overflow: auto;
    z-index: ${maxZIndex};
`;

export const ModalNavigation = styled.div`
    position: fixed;
    height: 48px;
    width: 100%;
    top: 0;
    z-index: 1;
    transition: all 0.2s ease-in;
`;

export const ModalCloseButton = styled.button`
    ${buttonReset}
    right: 18px;
    top: 6px;
    cursor: pointer;
    position: absolute;
    background: var(--color-dark-foreground-5);
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.5);

    &:focus-visible {
        outline: var(--focus-outline);
    }

    svg {
        width: 16px;
        height: 16px;

        path {
            fill: ${(props) => props.theme.brandedConfig.high_contrast};
        }
    }

    .SVGInline {
        width: 16px;
        height: 16px;
    }

    ${MQ(Breakpoints.s)} {
        right: 26px;
        top: 16px;
    }

    ${MQ(Breakpoints.m)} {
        right: 46px;
    }

    &:hover {
        background: var(--color-dark-foreground-15);

        svg {
            color: var(--color-main-white);
        }
    }
`;
